import { createFileRoute, Outlet, useParams } from '@tanstack/react-router'
import { useState } from 'react'
import {
  CELL_SLOTS,
  type PoolCapacityView,
  poolCapacityState,
  poolLinesDrift,
  poolPromise,
  type ReservationView,
  reservationFree,
  reservationStatus,
  useCapacity,
  useReservations,
} from '@/entities/capacity'
import { cn, errorMessage, formatDateTime, formatDurationSec } from '@/shared/lib'
import { Button } from '@/shared/ui/button'
import { Card, CardLink } from '@/shared/ui/card'
import { EmptyState } from '@/shared/ui/empty-state'
import { Icon } from '@/shared/ui/icon'
import { PageHeader } from '@/shared/ui/page-header'
import { PillTabs } from '@/shared/ui/pill-tabs'
import { SearchInput } from '@/shared/ui/search-input'
import { Spinner } from '@/shared/ui/spinner'
import { StatusBadge } from '@/shared/ui/status-badge'
import { ReservationForm } from '@/widgets/reservation-form'

export const Route = createFileRoute('/capacity')({
  component: CapacityPage,
  staticData: { crumb: 'Ёмкость' },
})

type Scope = 'active' | 'all'

// Экспорт нужен компонентному тесту: сам роут собирает его из этого же модуля.
export function CapacityPage() {
  const [scope, setScope] = useState<Scope>('active')
  const [search, setSearch] = useState('')
  const [createOpen, setCreateOpen] = useState(false)
  const { reservationId } = useParams({ strict: false })

  const capacity = useCapacity()
  const { reservations, isPending, isError, error, refetch } = useReservations({
    activeOnly: scope === 'active',
  })

  const q = search.trim().toLowerCase()
  const visible = reservations.filter(
    (r) =>
      !q ||
      r.id.toLowerCase().includes(q) ||
      r.pool.toLowerCase().includes(q) ||
      (r.account_id ?? '').toLowerCase().includes(q),
  )

  return (
    <>
      <PageHeader
        title="Ёмкость"
        description="Заявка — единственный вход в железо: под неё ядро поднимает ячейки и держит их, пока не придёт отзыв"
        actions={
          <Button variant="accent" onClick={() => setCreateOpen(true)}>
            <Icon name="plus" size={18} />
            Завести заявку
          </Button>
        }
      />

      <div className="flex flex-col gap-6">
        <PoolsBoard capacity={capacity} />

        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-title font-medium">Заявки на ёмкость</h2>
            <div className="flex flex-wrap items-center gap-3">
              <PillTabs
                aria-label="Отбор заявок"
                value={scope}
                onChange={setScope}
                options={[
                  { value: 'active', label: 'Живые' },
                  { value: 'all', label: 'Все' },
                ]}
              />
              <SearchInput
                placeholder="Заявка, пул или аккаунт…"
                className="w-72"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Поиск заявок"
              />
            </div>
          </div>

          {isPending ? (
            <div className="flex justify-center py-24 text-muted">
              <Spinner size={28} />
            </div>
          ) : isError ? (
            <EmptyState
              icon="alert"
              title="Не удалось загрузить заявки"
              description={errorMessage(error)}
              action={
                <Button size="sm" onClick={refetch}>
                  Повторить
                </Button>
              }
            />
          ) : reservations.length === 0 ? (
            <EmptyState
              icon="bolt"
              title="Заявок нет"
              description="Без заявки ядро не поднимает ни одной ячейки — и ничего не стоит"
              action={
                <Button variant="accent" size="sm" onClick={() => setCreateOpen(true)}>
                  Завести заявку
                </Button>
              }
            />
          ) : (
            <div className="flex items-start gap-6">
              <div className="flex w-[400px] shrink-0 flex-col gap-2">
                {visible.length === 0 && (
                  <EmptyState icon="search" title="Ничего не нашлось" description="Смени запрос" />
                )}
                {visible.map((reservation) => (
                  <ReservationCard
                    key={reservation.id}
                    reservation={reservation}
                    active={reservationId === reservation.id}
                  />
                ))}
              </div>
              <div className="min-w-0 flex-1">
                <Outlet />
              </div>
            </div>
          )}
        </div>
      </div>

      <ReservationForm
        key={String(createOpen)}
        open={createOpen}
        pools={capacity.pools}
        cellSlots={capacity.cellSlots || CELL_SLOTS}
        onClose={() => setCreateOpen(false)}
      />
    </>
  )
}

// Картина ёмкости по пулам: договорной потолок против того, что ядро реально
// способно поднять, продаваемые слоты, занятость и глубина очереди.
function PoolsBoard({ capacity }: { capacity: ReturnType<typeof useCapacity> }) {
  if (capacity.isPending) {
    return (
      <div className="flex justify-center py-12 text-muted">
        <Spinner size={24} />
      </div>
    )
  }
  if (capacity.isError) {
    return (
      <EmptyState
        icon="alert"
        title="Картина ёмкости недоступна"
        description={errorMessage(capacity.error)}
        action={
          <Button size="sm" onClick={capacity.refetch}>
            Повторить
          </Button>
        }
      />
    )
  }
  if (capacity.pools.length === 0) {
    return (
      <EmptyState
        icon="layers"
        title="Пулов нет"
        description="Ёмкость считается по пулам телефонии — заведи пул в разделе «Телефония»"
      />
    )
  }

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 className="text-title font-medium">Ёмкость по пулам</h2>
        <p className="text-caption text-muted">
          Квант — ячейка 1C + 2L на {capacity.cellSlots} звонка · прогрев ≈{' '}
          {formatDurationSec(capacity.leadTimeS)}
        </p>
      </div>
      <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-2 2xl:grid-cols-3">
        {capacity.pools.map((pool) => (
          <PoolCapacityCard key={pool.pool} pool={pool} />
        ))}
      </div>
    </section>
  )
}

function PoolCapacityCard({ pool }: { pool: PoolCapacityView }) {
  const drift = poolLinesDrift(pool)
  const state = poolCapacityState(pool.state)
  const promise = poolPromise(pool)
  const busyShare = pool.slots_total > 0 ? pool.slots_busy / pool.slots_total : 0

  return (
    // Именованная область: карточек пулов на доске много, и каждой нужно имя —
    // и скринридеру, и тесту.
    <Card role="group" aria-label={`Пул ${pool.pool}`} className="flex flex-col gap-4 p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-body-md font-medium">{pool.pool_name || pool.pool}</p>
          <p className="truncate font-mono text-caption text-muted">{pool.pool}</p>
          <p className="text-caption text-faint">
            ячейки: готовых {pool.cells.ready} · в сборке {pool.cells.assembling} · в дренаже{' '}
            {pool.cells.draining}
          </p>
        </div>
        <StatusBadge {...state} />
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Metric label="Оплачено каналов" value={pool.lines_capacity} />
        <Metric
          label="Ядро поднимет"
          value={pool.usable_lines}
          tone={drift.wastedChannels > 0 ? 'warn' : undefined}
          note={drift.wastedChannels > 0 ? `−${drift.wastedChannels} впустую` : undefined}
        />
        <Metric
          label="Продаётся"
          value={pool.sellable_slots}
          note={drift.unsellableLines > 0 ? `остаток ${drift.unsellableLines}` : undefined}
        />
      </div>

      <div>
        <div className="mb-1 flex items-baseline justify-between gap-3 text-caption">
          <span className="text-muted">
            Обещано заявкам{' '}
            <span
              className={cn(
                'font-medium',
                promise === 'shrunk'
                  ? 'text-danger'
                  : promise === 'queued'
                    ? 'text-warn'
                    : 'text-app-fg',
              )}
            >
              {pool.reserved_slots}
            </span>{' '}
            из {pool.sellable_slots}
          </span>
          {pool.waiting_slots > 0 && (
            <span className="text-warn">{pool.waiting_slots} ждут линий</span>
          )}
        </div>
        {/* Полоса дублирует числа выше — скринридеру она лишний шум. */}
        <div aria-hidden="true" className="h-1.5 overflow-hidden rounded-full bg-raise">
          <div
            className={cn(
              'h-full rounded-full',
              promise === 'shrunk' ? 'bg-danger' : promise === 'queued' ? 'bg-warn' : 'bg-accent',
            )}
            style={{
              width: `${Math.min(100, pool.sellable_slots > 0 ? (pool.reserved_slots / pool.sellable_slots) * 100 : 0)}%`,
            }}
          />
        </div>
        {promise === 'shrunk' && (
          <p className="mt-2 text-caption text-danger">
            Ёмкость просела ниже обещанного заявкам — линии ушли уже после их приёма.
          </p>
        )}
        {promise === 'queued' && (
          <p className="mt-2 text-caption text-muted">
            Обещано больше, чем пул продаёт: часть заявок стоит в очереди за чужими линиями и железа
            под собой не имеет.
          </p>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-caption text-muted">
        {/* slots_free пула — не право на слоты, а операторская диагностика: он
            принадлежит чьей-то заявке, и «свободный» — это чужой простой. */}
        <span title="Диагностика флота: свободный слот принадлежит чужой заявке, а не доступен к заказу">
          Флот: занято{' '}
          <span className="font-medium text-app-fg tabular-nums">
            {pool.slots_busy}/{pool.slots_total}
          </span>{' '}
          ({Math.round(busyShare * 100)}%)
        </span>
        <span>
          Линий арендовано <span className="tabular-nums">{pool.lines_leased}</span>
        </span>
        <PoolQueue pool={pool} />
      </div>
    </Card>
  )
}

/**
 * Глубина очереди пула. «Брокер не ответил» и «очередь честно пуста» — разные
 * новости: первое чинят, второе значит, что заданий нет и это нормально.
 * Различает их `queue_available`, а не пустое число.
 */
function PoolQueue({ pool }: { pool: PoolCapacityView }) {
  if (!pool.queue_available) {
    return (
      <span
        className="text-warn"
        title="JetStream cp не ответил на опрос — глубина неизвестна, а не равна нулю"
      >
        Очередь: <span className="font-medium">брокер не ответил</span>
      </span>
    )
  }
  const depth = pool.queue_depth ?? 0
  if (depth === 0) {
    return (
      <span>
        Очередь: <span className="text-muted">пуста</span> — заданий в ней нет
      </span>
    )
  }
  return (
    <span>
      Очередь: <span className="font-medium tabular-nums text-app-fg">{depth}</span>
      {pool.queue_oldest_age_s !== undefined && pool.queue_oldest_age_s > 0 && (
        <> · старшему {formatDurationSec(pool.queue_oldest_age_s)}</>
      )}
    </span>
  )
}

function Metric({
  label,
  value,
  note,
  tone,
}: {
  label: string
  value: number
  note?: string | undefined
  tone?: 'warn' | undefined
}) {
  return (
    <div className="min-w-0">
      <p className="truncate text-caption text-muted">{label}</p>
      <p
        className={cn(
          'text-heading-sm font-light tabular-nums',
          tone === 'warn' ? 'text-warn' : undefined,
        )}
      >
        {value}
      </p>
      {note && <p className="truncate text-caption text-muted">{note}</p>}
    </div>
  )
}

function ReservationCard({
  reservation,
  active,
}: {
  reservation: ReservationView
  active: boolean
}) {
  const status = reservationStatus(reservation.status)
  const dim = active ? 'text-white/70' : 'text-muted'
  const ready = reservation.slots > 0 ? reservation.slots_ready / reservation.slots : 0

  return (
    <CardLink
      to="/capacity/$reservationId"
      params={{ reservationId: reservation.id }}
      active={active}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="truncate font-mono text-body-sm font-medium">{reservation.id}</span>
        <StatusBadge {...status} />
      </div>
      <p className={cn('mt-0.5 truncate text-caption', dim)}>
        {reservation.account_id || 'операторская заявка'} · пул{' '}
        {reservation.pool_name || reservation.pool}
      </p>

      <div className={cn('mt-3 rounded-control px-3 py-2', active ? 'bg-black/25' : 'bg-raise')}>
        <div className="flex items-baseline justify-between gap-2 text-caption">
          <span className={dim}>Готово слотов</span>
          <span className="tabular-nums">
            <span className={cn('font-medium', active ? 'text-white' : 'text-app-fg')}>
              {reservation.slots_ready}
            </span>
            <span className={dim}> из {reservation.slots}</span>
          </span>
        </div>
        <div
          aria-hidden="true"
          className={cn(
            'mt-1.5 h-1.5 overflow-hidden rounded-full',
            active ? 'bg-black/30' : 'bg-card',
          )}
        >
          <div
            className={cn('h-full rounded-full', active ? 'bg-white/80' : 'bg-accent')}
            style={{ width: `${Math.min(100, ready * 100)}%` }}
          />
        </div>
      </div>

      <div className={cn('mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-caption', dim)}>
        {reservation.slots_busy > 0 && <span>в разговоре {reservation.slots_busy}</span>}
        {reservation.overdue && (
          <span className={active ? 'text-white' : 'text-warn'}>просрочена</span>
        )}
        {(reservation.waiting_for ?? []).length > 0 && (
          <span>ждёт {(reservation.waiting_for ?? []).length} заявку по линиям</span>
        )}
        {(reservation.cells ?? []).length > 0 && (
          <span>ячеек {(reservation.cells ?? []).length}</span>
        )}
        {reservationFree(reservation.status) && <span>железа нет — бесплатна</span>}
        <span>до {formatDateTime(reservation.planned_until)}</span>
      </div>
    </CardLink>
  )
}
